import { formatDateToMDY } from '../../../constants/utils';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useCreatePromotionHook } from '../hooks/useCreatePromotionHook';
import { Grid, Typography, Card, Button, Snackbar, Tooltip } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import SelectDropdown from '../../../components/SelectDropdown';
import styles from './styles';
import DatePickerMui from '../../../components/DatePickerMui';
import Chip from '@mui/material/Chip';
import HeightIcon from '@mui/icons-material/Height';
import DiscountOffer from './DiscountOffer';
import PromotionPreview from './PromotionPreview';
import FinancialOffer from './FinancialOffer';
import OfferSweetners from './OfferSweetners';
import { ADD_FINANCE_OFFER, OR, REMOVE_FINANCE_OFFER } from '../constants';
import Alert from '../../../components/Alert';
import PlusIcon from '@mui/icons-material/Add';
import MinusIcon from '@mui/icons-material/Remove';
import Notes from './Notes';
import { ShimmerComponent } from '../../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../../components/Shimmer/BouncingLogo';
import { ROUTES } from '../../../constants/routes';

const CreatePromotions = () => {
  const { userStores, isAdmin } = useSelector((state) => state.auth);
  const storeOptions = useMemo(() => {
    const filteredStores = userStores.filter((store) => store.branch_name !== 'GALE Markets');
    const stores = filteredStores.map((store) => ({
      label: store.branch_name,
      value: store.branch_number,
      ...store,
    }));
    return stores.sort((a, b) => a.label.localeCompare(b.label));
  }, [userStores]);
  const params = useParams();
  const navigate = useNavigate();
  const { id } = params;
  const [canShowFlipButton, setShowFlipButton] = useState(false);
  const [status, setStatus] = useState('Open');
  const [mode, setMode] = useState(id ? 'Edit' : 'Create');
  const [flipOffer, setFlipOffer] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]);
  const [isDiscountClickable, setIsDiscountClickable] = useState(false);
  const [isFinancialClickable, setIsFinancialClickable] = useState(false);
  const [isNotesClickable, setIsNotesClickable] = useState(false);
  const [selectedConjunction, setSelectedConjunction] = useState(OR);
  const [disableUPDATEBtn, setDisableUPDATEBtn] = useState(true);
  const [disableDRAFTBtn, setDisableDRAFTBtn] = useState(true);
  const { addDays, set } = require('date-fns');
  const currentDate = new Date();
  const [defaultStartDate, setDefaultStartDate] = useState(addDays(currentDate, 5));
  const [defaultEndDate, setDefaultEndDate] = useState(addDays(currentDate, 10));
  const [selectedStartDate, setSelectedStartDate] = useState(formatDateToMDY(defaultStartDate));
  const [selectedEndDate, setSelectedEndDate] = useState(formatDateToMDY(defaultEndDate));
  const [historicPromotionData, setHistoricPromotionData] = useState(null);
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [snackBarSeverity, setSnackBarSeverity] = useState('success');
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const {
    promotionData,
    setPromotionData,
    productsList,
    discountOffersList,
    financeOffersList,
    sweetnerOffersList,
    handleCreatePromotion,
    handleSavePromotion,
    handlePublishPromotion,
    handleDraftPromotion,
    previewData,
    loadedPromotionData,
    showBtnLoader,
    dataLoader,
    fetchHeadlineData,
    handleNotifyEmail,
    notifyEmail,
    showDraftBtnLoader,
    showUpdateBtnLoader,
    headlineLoading,
  } = useCreatePromotionHook({
    id,
    setStatus,
    setIsDiscountClickable,
    setIsFinancialClickable,
    setSelectedStores,
    setSelectedConjunction,
    setShowFlipButton,
    setFlipOffer,
    setDisableDRAFTBtn,
    setDisableUPDATEBtn,
    mode,
    setSelectedStartDate,
    setSelectedEndDate,
    historicPromotionData,
    setHistoricPromotionData,
    setShowSnackBar,
    setSnackBarSeverity,
    setSnackBarMessage,
    setIsNotesClickable,
  });
  const [minEndDate, setMinEndDate] = useState(defaultStartDate);
  const duplicatePromotionEnable =
    mode === 'Edit' && promotionData?.is_duplicate && promotionData?.status === 'OPEN';
  const disableDateSelection = duplicatePromotionEnable
    ? false
    : isAdmin &&
      (promotionData?.promotion_type === 'DEFAULT' ||
        promotionData?.promotion_type === 'SPECIAL') &&
      mode !== 'Create';
  useEffect(() => {
    if (mode === 'Create') {
      setPromotionData({
        start_date: formatDateToMDY(defaultStartDate),
        freeze_date: formatDateToMDY(defaultStartDate),
        end_date: formatDateToMDY(defaultEndDate),
      });
    }
  }, []);

  useEffect(() => {
    if (
      loadedPromotionData &&
      historicPromotionData !== null &&
      Object.keys(historicPromotionData).length !== 0
    ) {
      setDefaultStartDate(formatDateToMDY(new Date(historicPromotionData.start_date)));
      setDefaultEndDate(formatDateToMDY(new Date(historicPromotionData.end_date)));
      setMinEndDate(formatDateToMDY(new Date(historicPromotionData.start_date)));
      setPromotionData(historicPromotionData);
    }
  }, [loadedPromotionData, historicPromotionData]);

  function handleAddFinanceOffer() {
    let newFinanceOffer = {};
    let financeOffers = [...promotionData.finance_offer];
    if (promotionData.finance_offer.length === 2) {
      financeOffers.pop();
    } else {
      financeOffers.splice(1, 0, newFinanceOffer);
    }
    setPromotionData({
      ...promotionData,
      finance_offer: financeOffers,
    });
  }

  const handleFlipButtonClick = () => {
    setPromotionData({ ...promotionData, flip: !flipOffer });
    setFlipOffer(!flipOffer);
  };

  const handleStartDateChange = (date) => {
    const formattedDate = formatDateToMDY(date);
    setDefaultStartDate(formattedDate);
    setMinEndDate(formattedDate);
    setSelectedStartDate(formattedDate);
    setPromotionData({ ...promotionData, start_date: formattedDate, freeze_date: formattedDate });
  };

  const handleEndDateChange = (date) => {
    const formattedDate = formatDateToMDY(date);
    setDefaultEndDate(formattedDate);
    setSelectedEndDate(formattedDate);
    setPromotionData({ ...promotionData, end_date: formattedDate });
  };

  const handleStoreChange = (value) => {
    const filteredValue = value.filter((v) => v !== null && v !== undefined);
    if (filteredValue?.length) {
      setIsDiscountClickable(true);
      setIsFinancialClickable(true);
      setIsNotesClickable(true);
      setPromotionData({ ...promotionData, store_ids: filteredValue });
    } else {
      setIsDiscountClickable(false);
      setIsFinancialClickable(false);
      setIsNotesClickable(false);
    }
    setSelectedStores(filteredValue);
    if (mode !== 'Create' && filteredValue?.length) {
      setDisableUPDATEBtn(false);
      setDisableDRAFTBtn(false);
    }
  };
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setShowSnackBar(false);
  };

  // Helper function to determine the minimum end date
  const getMinEndDate = () => {
    if (promotionData?.status === 'FROZEN' && promotionData?.status === 'ARCHIVED') {
      return currentDate;
    } else if (promotionData?.start_date) {
      return new Date(promotionData.start_date);
    } else {
      return minEndDate;
    }
  };

  return (
    <>
      {dataLoader && (!loadedPromotionData || mode === 'Create') && (
        <ShimmerComponent
          updatedStyle={{
            width: '90%',
            height: '100%',
            backgroundSize: '2000px 800px',
            border: '0px',
            margin: 'auto',
            padding: '0 20px',
          }}
        >
          <BouncingLogo height="88vh" text="Loading..." />
        </ShimmerComponent>
      )}
      <section>
        <Button
          startIcon={<ArrowBack sx={{ color: 'black' }} />}
          onClick={() => navigate(ROUTES.promotions)}
          sx={{ display: 'flex', padding: '0px 20px', color: 'black', fontWeight: 700 }}
        >
          Back
        </Button>
      </section>
      {!dataLoader && (loadedPromotionData || mode === 'Create') && (
        <Grid
          container
          spacing={4}
          sx={styles.gridContainer}
          style={{ height: '87vh', overflowY: 'scroll' }}
        >
          <Grid item xs={6} style={{ maxHeight: '100%', overflowY: 'auto', padding: '0 32px' }}>
            <section
              style={
                promotionData?.promotion_type
                  ? { ...styles.sectionSpacing }
                  : { ...styles.sectionSpacing, paddingTop: '0' }
              }
            >
              <Typography sx={styles.heading}>Promotions Dates</Typography>
              <Card
                sx={
                  isAdmin ||
                  (!isAdmin &&
                    promotionData?.status !== 'ARCHIVED' &&
                    promotionData?.status !== 'FROZEN') ||
                  !duplicatePromotionEnable
                    ? styles.cardBox
                    : { ...styles.disableCard, ...styles.cardBox }
                }
              >
                <Grid container spacing={1} sx={{ padding: '20px' }}>
                  <Grid item className="promotion-start-dt" sx={{ width: 'calc(100%/2)' }}>
                    <DatePickerMui
                      label={'Start Date'}
                      handleDateChange={handleStartDateChange}
                      disabled={!isAdmin || disableDateSelection}
                      defaultValue={null}
                      disablePast={
                        promotionData?.status !== 'FROZEN' && promotionData?.status !== 'ARCHIVED'
                      }
                      sx={{ width: 'calc(100% - 150px/2)' }}
                      minDate={currentDate}
                      updateDateValue={selectedStartDate}
                    />
                  </Grid>
                  <Grid item className="promotion-end-dt" sx={{ width: 'calc(100%/2)' }}>
                    <DatePickerMui
                      label={'End Date'}
                      handleDateChange={handleEndDateChange}
                      passUnformattedDate={true}
                      disabled={!isAdmin || disableDateSelection}
                      defaultValue={null}
                      minDate={getMinEndDate()}
                      disablePast
                      sx={{ width: 'calc(100% - 150px/2)' }}
                      updateDateValue={selectedEndDate}
                    />
                  </Grid>
                </Grid>
              </Card>
            </section>
            <section
              style={
                promotionData?.status === 'ARCHIVED'
                  ? { ...styles.disableCard, ...styles.sectionSpacing }
                  : styles.sectionSpacing
              }
            >
              <Typography style={styles.heading}>Branches</Typography>
              <Card sx={styles.cardBox} style={{ overflow: 'visible' }}>
                {(mode !== 'Create' && promotionData?.store_id) || mode === 'Create' ? (
                  <SelectDropdown
                    isSelectAll={true}
                    multiple={true}
                    options={storeOptions}
                    label={'Please Select all the branches this offers applies to'}
                    multiSelectDefaultValues={
                      selectedStores?.length > 0 ? selectedStores : [promotionData?.store_id]
                    }
                    handleFilterChange={handleStoreChange}
                    updateMultipleDropdownValue={
                      selectedStores?.length > 0 ? selectedStores : [promotionData?.store_id]
                    }
                    width={'99%'}
                    disabled={mode !== 'Create' || headlineLoading}
                  />
                ) : null}
              </Card>
            </section>
            <DiscountOffer
              offersList={discountOffersList}
              defaultExpiryDate={defaultEndDate}
              promotionData={promotionData}
              setPromotionData={setPromotionData}
              mode={mode}
              isDiscountClickable={isDiscountClickable}
              isAdmin={isAdmin}
              productsList={productsList}
              setDisableDRAFTBtn={setDisableDRAFTBtn}
              setDisableUPDATEBtn={setDisableUPDATEBtn}
              loadedPromotionData={loadedPromotionData}
              isDateSelectionDisabled={headlineLoading}
              fetchHeadlineData={fetchHeadlineData}
            />
            <FinancialOffer
              index={0}
              offersList={financeOffersList}
              defaultExpiryDate={defaultEndDate}
              promotionData={promotionData}
              setPromotionData={setPromotionData}
              mode={mode}
              isFinancialClickable={isFinancialClickable}
              isAdmin={isAdmin}
              selectedConjunction={selectedConjunction}
              setSelectedConjunction={setSelectedConjunction}
              setDisableDRAFTBtn={setDisableDRAFTBtn}
              setDisableUPDATEBtn={setDisableUPDATEBtn}
              loadedPromotionData={loadedPromotionData}
              selectedStores={selectedStores}
              historicPromotionData={historicPromotionData}
              isDateSelectionDisabled={headlineLoading}
              fetchHeadlineData={fetchHeadlineData}
            />
            {promotionData?.finance_offer?.length === 2 && (
              <FinancialOffer
                index={1}
                offersList={financeOffersList}
                defaultExpiryDate={defaultEndDate}
                promotionData={promotionData}
                setPromotionData={setPromotionData}
                mode={mode}
                isFinancialClickable={isFinancialClickable}
                isAdmin={isAdmin}
                selectedConjunction={selectedConjunction}
                setSelectedConjunction={setSelectedConjunction}
                setDisableDRAFTBtn={setDisableDRAFTBtn}
                setDisableUPDATEBtn={setDisableUPDATEBtn}
                loadedPromotionData={loadedPromotionData}
                selectedStores={selectedStores}
                historicPromotionData={historicPromotionData}
                isDateSelectionDisabled={headlineLoading}
                fetchHeadlineData={fetchHeadlineData}
              />
            )}
            {promotionData?.finance_offer?.length >= 1 && (
              <Tooltip
                arrow
                placement="top"
                leaveDelay={500}
                sx={{
                  popper: styles.freezeDateTooltip,
                }}
                title={
                  promotionData?.discount_offer !== undefined &&
                  promotionData?.discount_offer !== null
                    ? 'Cannot add finance offer when discount offer is present'
                    : ''
                }
              >
                <span>
                  <Button
                    sx={{
                      ...styles.buttonStyle,
                      background: '#e0e0e0',
                      color: '#000',
                      width: 'fit-content',
                    }}
                    disabled={
                      promotionData?.discount_offer !== undefined &&
                      promotionData?.discount_offer !== null
                    }
                    startIcon={
                      promotionData?.finance_offer?.length === 2 ? <MinusIcon /> : <PlusIcon />
                    }
                    onClick={handleAddFinanceOffer}
                  >
                    {promotionData?.finance_offer?.length === 2
                      ? REMOVE_FINANCE_OFFER
                      : ADD_FINANCE_OFFER}
                  </Button>
                </span>
              </Tooltip>
            )}

            <OfferSweetners
              offersList={sweetnerOffersList}
              defaultExpiryDate={defaultEndDate}
              promotionData={promotionData}
              setPromotionData={setPromotionData}
              mode={mode}
              isAdmin={isAdmin}
              loadedPromotionData={loadedPromotionData}
              isDateSelectionDisabled={headlineLoading}
            />

            <Notes
              promotionData={promotionData}
              mode={mode}
              isNotesClickable={isNotesClickable}
              setPromotionData={setPromotionData}
              fetchHeadlineData={fetchHeadlineData}
              loadedPromotionData={loadedPromotionData}
            />
          </Grid>
          <Grid
            item
            xs={6}
            sx={styles.offer}
            style={{ padding: '0 32px', height: '700px', overflowY: 'auto' }}
          >
            <Grid container sx={{ marginBottom: '15px' }} justifyContent="space-between">
              <Grid item xs={5}>
                <Typography sx={{ ...styles.heading, ...styles.promoPreview }}>
                  Promotion Preview
                </Typography>
              </Grid>
              <Grid
                item
                xs={7}
                container
                justifyContent={canShowFlipButton ? 'space-between' : 'flex-end'}
              >
                <Grid item sx={{ width: 'fit-content', paddingRight: '15px' }}>
                  {canShowFlipButton && (
                    <Tooltip
                      arrow
                      placement="top"
                      leaveDelay={500}
                      sx={{
                        popper: styles.popper,
                      }}
                      style={{ marginTop: '10px' }}
                      title={`Flip to lead with ${flipOffer ? 'discount' : 'financing'} offer`}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleFlipButtonClick}
                        startIcon={<HeightIcon />}
                      >
                        Flip offer
                      </Button>
                    </Tooltip>
                  )}
                </Grid>
                <Grid
                  item
                  container
                  sx={{ width: 'fit-content' }}
                  style={{ marginTop: '10px' }}
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  <Typography>Status: &#x200A;&#x200D; </Typography>
                  <Chip sx={styles[status?.toLowerCase()]} label={status} />
                </Grid>
              </Grid>
            </Grid>
            <Card sx={{ ...styles.preview, ...styles.card }}>
              <PromotionPreview
                data={previewData}
                promotionData={promotionData}
                flipValue={flipOffer}
                handleCreatePromotion={handleCreatePromotion}
                handleSavePromotion={handleSavePromotion}
                handleDraftPromotion={handleDraftPromotion}
                handlePublishPromotion={handlePublishPromotion}
                mode={mode}
                isAdmin={isAdmin}
                showBtnLoader={showBtnLoader}
                promotionStatus={status}
                disableDRAFTBtn={disableDRAFTBtn}
                disableUPDATEBtn={disableUPDATEBtn}
                selectedStores={selectedStores}
                defaultEndDate={defaultEndDate}
                defaultStartDate={defaultStartDate}
                selectedConjunction={selectedConjunction}
                handleNotifyEmail={handleNotifyEmail}
                notifyEmail={notifyEmail}
                showDraftBtnLoader={showDraftBtnLoader}
                showUpdateBtnLoader={showUpdateBtnLoader}
              />
            </Card>
          </Grid>
          <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            open={showSnackBar}
            autoHideDuration={3000}
            onClose={handleClose}
          >
            <Alert severity={snackBarSeverity}>{snackBarMessage}</Alert>
          </Snackbar>
        </Grid>
      )}
    </>
  );
};
export default CreatePromotions;
